import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, FileText, ExternalLink, Upload, Loader2 } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var documentTypes = ["contract", "deed", "permit", "invoice", "report"];

var emptyForm = {
  name: "",
  document_type: "contract",
  property_id: "",
  unit_id: "",
  description: "",
  file_url: "",
  expiry_date: "",
};

export default function Documents() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [documents, setDocuments] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [filterUnits, setFilterUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingDoc, setEditingDoc] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterProperty, setFilterProperty] = useState("all");
  var [filterUnit, setFilterUnit] = useState("all");
  var [uploading, setUploading] = useState(false);
  var [storageConfigured, setStorageConfigured] = useState(null);

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { 
    fetchData(); 
    checkStorageConfig();
  }, []);

  // Fetch units when form property changes (for dialog)
  useEffect(function() {
    if (form.property_id && form.property_id !== "none") {
      fetchUnits(form.property_id);
    } else {
      setUnits([]);
      setForm(function(f) { return { ...f, unit_id: "" }; });
    }
  }, [form.property_id]);

  // Fetch units when filter property changes
  useEffect(function() {
    if (filterProperty && filterProperty !== "all") {
      fetchFilterUnits(filterProperty);
    } else {
      setFilterUnits([]);
      setFilterUnit("all");
    }
  }, [filterProperty]);

  async function fetchData() {
    try {
      var docsRes = await fetch(API_URL + "/api/documents", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var propertiesRes = await fetch(API_URL + "/api/properties", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function fetchFilterUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setFilterUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching filter units:", error);
    }
  }

  async function checkStorageConfig() {
    try {
      var response = await fetch(API_URL + "/api/storage/config", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      if (response.ok) {
        var data = await response.json();
        setStorageConfigured(data.configured);
      }
    } catch (err) {
      setStorageConfigured(false);
    }
  }

  async function handleFileUpload(e) {
    var file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      var formData = new FormData();
      formData.append("file", file);
      formData.append("entity_type", "document");
      formData.append("folder", "documents");
      
      var response = await fetch(API_URL + "/api/storage/upload", {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body: formData
      });
      
      if (response.ok) {
        var result = await response.json();
        setForm({ ...form, file_url: result.file_url });
        toast.success(t("fileUpload.uploaded", { name: file.name }));
      } else {
        var error = await response.json();
        toast.error(error.detail || t("fileUpload.uploadFailed"));
      }
    } catch (err) {
      toast.error(t("fileUpload.uploadFailed"));
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingDoc ? API_URL + "/api/documents/" + editingDoc.document_id : API_URL + "/api/documents";
      var method = editingDoc ? "PUT" : "POST";

      var payload = {
        name: form.name,
        document_type: form.document_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        description: form.description || null,
        file_url: form.file_url || null,
        expiry_date: form.expiry_date || null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingDoc ? t("common.success") : t("common.success"));
        setDialogOpen(false);
        setEditingDoc(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(doc) {
    setEditingDoc(doc);
    setForm({
      name: doc.name || "",
      document_type: doc.document_type || "contract",
      property_id: doc.property_id || "",
      unit_id: doc.unit_id || "",
      description: doc.description || "",
      file_url: doc.file_url || "",
      expiry_date: doc.expiry_date || "",
    });
    // Fetch units for the selected property
    if (doc.property_id) {
      fetchUnits(doc.property_id);
    }
    setDialogOpen(true);
  }

  async function handleDelete(docId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/documents/" + docId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchData();
      } else {
        toast.error(t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getPropertyName(propertyId) {
    if (!propertyId) return "-";
    for (var i = 0; i < properties.length; i++) {
      if (properties[i].property_id === propertyId) return properties[i].name;
    }
    return "-";
  }

  function getUnitName(unitId) {
    if (!unitId) return "-";
    for (var i = 0; i < units.length; i++) {
      if (units[i].unit_id === unitId) return units[i].name;
    }
    return unitId.substring(0, 8) + "...";
  }

  function getFilteredDocuments() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < documents.length; i++) {
      var d = documents[i];
      var matchName = d.name && d.name.toLowerCase().indexOf(searchLower) >= 0;
      var matchType = d.document_type && d.document_type.toLowerCase().indexOf(searchLower) >= 0;
      var matchSearch = matchName || matchType;
      var matchProperty = filterProperty === "all" || d.property_id === filterProperty;
      var matchUnit = filterUnit === "all" || d.unit_id === filterUnit;
      if (matchSearch && matchProperty && matchUnit) result.push(d);
    }
    return result;
  }

  function renderFilterPropertyOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderFilterUnitOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < filterUnits.length; i++) {
      items.push(<SelectItem key={filterUnits[i].unit_id} value={filterUnits[i].unit_id}>{filterUnits[i].name}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderDocumentTypeOptions() {
    var items = [];
    for (var i = 0; i < documentTypes.length; i++) {
      items.push(<SelectItem key={documentTypes[i]} value={documentTypes[i]}>{t("documents.types." + documentTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderDocumentRows() {
    var filteredDocuments = getFilteredDocuments();
    var rows = [];
    for (var i = 0; i < filteredDocuments.length; i++) {
      var doc = filteredDocuments[i];
      var docId = doc.document_id;
      var docData = JSON.parse(JSON.stringify(doc));
      rows.push(
        <TableRow key={docId} data-testid={"document-row-" + docId}>
          <TableCell className="font-medium">{doc.name}</TableCell>
          <TableCell><Badge variant="outline">{t("documents.types." + doc.document_type)}</Badge></TableCell>
          <TableCell>{getPropertyName(doc.property_id)}</TableCell>
          <TableCell>{doc.unit_id ? <Badge variant="secondary">{getUnitName(doc.unit_id)}</Badge> : "-"}</TableCell>
          <TableCell>{doc.expiry_date || "-"}</TableCell>
          <TableCell>
            {doc.file_url ? (
              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                <ExternalLink className="w-4 h-4" /> {t("documents.view")}
              </a>
            ) : "-"}
          </TableCell>
          {isAdmin && (
            <TableCell>
              {doc.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{doc.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{doc.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, docData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, docId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  var filteredDocuments = getFilteredDocuments();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("documents.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("documents.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingDoc(null); setForm(emptyForm); setUnits([]); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-document-btn"><Plus className="w-4 h-4 mr-2" />{t("documents.addDocument")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingDoc ? t("documents.editDocument") : t("documents.addNewDocument")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("documents.documentName")}</Label>
                  <Input value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="e.g., Lease Agreement" required data-testid="document-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("documents.documentType")}</Label>
                    <Select value={form.document_type} onValueChange={function(v) { setForm({ ...form, document_type: v }); }}>
                      <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderDocumentTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({ ...form, property_id: v, unit_id: "" }); }}>
                      <SelectTrigger data-testid="document-property-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("common.unit")}</Label>
                  <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({ ...form, unit_id: v }); }} disabled={!form.property_id || form.property_id === "none" || units.length === 0}>
                    <SelectTrigger data-testid="document-unit-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                    <SelectContent>{renderUnitOptions()}</SelectContent>
                  </Select>
                  {form.property_id && form.property_id !== "none" && units.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{t("common.noUnitsAvailable")}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">{t("documents.fileUrl")}</Label>
                  <div className="flex gap-2">
                    <Input value={form.file_url} onChange={function(e) { setForm({ ...form, file_url: e.target.value }); }} placeholder="https://..." data-testid="document-url-input" className="flex-1" />
                    {storageConfigured && (
                      <div className="relative">
                        <input 
                          type="file" 
                          onChange={handleFileUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                          disabled={uploading}
                        />
                        <Button type="button" variant="outline" disabled={uploading}>
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </Button>
                      </div>
                    )}
                  </div>
                  {storageConfigured === false && (
                    <p className="text-xs text-muted-foreground mt-1">{t("fileUpload.configureInSettings")}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">{t("documents.expiryDate")}</Label>
                  <Input type="date" value={form.expiry_date} onChange={function(e) { setForm({ ...form, expiry_date: e.target.value }); }} data-testid="document-expiry-input" />
                </div>
                <div>
                  <Label className="form-label">{t("common.description")}</Label>
                  <Textarea value={form.description} onChange={function(e) { setForm({ ...form, description: e.target.value }); }} placeholder={t("common.description")} rows={2} data-testid="document-description-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="document-submit-btn">{editingDoc ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="document-search-input" />
        </div>
        <Select value={filterProperty} onValueChange={function(v) { setFilterProperty(v); setFilterUnit("all"); }}>
          <SelectTrigger className="w-48" data-testid="document-filter-property"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
          <SelectContent>{renderFilterPropertyOptions()}</SelectContent>
        </Select>
        <Select value={filterUnit} onValueChange={setFilterUnit} disabled={filterProperty === "all" || filterUnits.length === 0}>
          <SelectTrigger className="w-48" data-testid="document-filter-unit"><SelectValue placeholder={t("common.unit")} /></SelectTrigger>
          <SelectContent>{renderFilterUnitOptions()}</SelectContent>
        </Select>
      </div>

      {filteredDocuments.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("common.unit")}</TableHead>
                <TableHead>{t("documents.expiryDate")}</TableHead>
                <TableHead>Link</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderDocumentRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("documents.noDocuments")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("documents.addFirstDocument")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
